import { withStory, WithStoryProps } from '@storyofams/storyblok-toolkit';
import { find } from 'lodash';
import { GetStaticProps, GetStaticPaths, NextPage } from 'next';

import { useRouter } from 'next/router';
import {
  enhancedStaticProps,
  getTranslationForLocale,
  isSameSlug,
  resolveCallToAction,
  withLayout,
} from '~lib';
import { DynamicPage } from '~components/dynamic';
import { Home as HomePage } from '~components/home';
import { getShopifySdk } from '~lib/shopify/client';
import { storyblokSdk } from '~lib/storyblok/client';
import { TermsandconditionsComponent } from '~lib/storyblok/sdk';

export interface PageProps extends WithStoryProps {
  termsAndConditions?: TermsandconditionsComponent;
  products?: any;
}

const Page: NextPage<PageProps> = ({ ...props }) => {
  const { query } = useRouter();
  /* Storyblok has a redirect bug on translated home page, 
  this renders home to allow them to view the dutch homepage in preview mode
  see: https://storyofams.atlassian.net/jira/servicedesk/projects/SLA/queues/custom/199/SLA-1109 */
  if (query?._storyblok_c === 'home') {
    return <HomePage {...props} />;
  }

  return <DynamicPage {...props} />;
};

export default withLayout(withStory(Page));

export const getStaticProps: GetStaticProps<PageProps> = enhancedStaticProps(
  async ({ defaultLocale, locale, params, sdk }) => {
    let story;
    let notFound = false;
    let termsAndConditions = null;
    const products = {};
    const { lang, needsTranslation } = getTranslationForLocale(
      locale,
      defaultLocale,
    );

    try {
      const id = `${needsTranslation ? `${lang}/` : ''}${params.slug}`;

      story = (
        await sdk.pageById({
          lang: needsTranslation ? lang : 'default',
          id,
        })
      ).DynamicpageItem;

      if (!isSameSlug(story?.full_slug, id)) {
        throw new Error('Not found');
      }

      await resolveCallToAction(story, sdk, lang, defaultLocale);
    } catch (e) {
      notFound = true;
    }

    try {
      termsAndConditions = (await sdk.termsAndConditionsItems())
        .TermsandconditionsItems;
    } catch (e) {
      notFound = true;
    }

    if (
      find(story?.content?.sections, ['component', 'products_dynamic'])
        ?.products?.item?.length
    ) {
      const shopifySdk = getShopifySdk(lang);

      for (let i = 0; i < story?.content?.sections.length; i++) {
        const value = story?.content?.sections[i];

        if (value.component === 'products_dynamic') {
          const productsArray = (
            await Promise.all(
              value.products.item
                ?.filter((p) => !!p.blob.handle)
                ?.map((p) =>
                  shopifySdk
                    .productByHandle({
                      // @ts-ignore
                      handle: p.blob.handle,
                    })
                    .then((res) => res.productByHandle)
                    .catch((err) => console.log(err)),
                ),
            )
          )?.filter((p) => !!p);

          products[i] = productsArray;
        }
      }
    }

    return {
      props: {
        products,
        story,
        termsAndConditions,
      },
      notFound: notFound || !story,
      revalidate: 60,
    };
  },
);

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const pages = (await storyblokSdk.dynamicPages()).DynamicpageItems;

  return {
    paths: pages?.items
      ?.filter(({ full_slug }) => !full_slug?.startsWith('cocktail-moments/'))
      ?.map(({ slug }) => ({
        slug,
      }))
      ?.map((params) => locales.map((locale) => ({ params, locale })))
      ?.flat(),
    fallback: 'blocking',
  };
};
